/**
 * MigrationController - Handles local-to-cloud data migration
 * 
 * Manages the migration modal that prompts users to migrate local patches
 * to cloud storage when they sign in. Subscribes to AUTH_CHANGED events
 * to trigger migration check when user logs in.
 */

import { MODAL_IDS } from '../constants/modals.js';
import { MIGRATION_PROMPTED_KEY } from '../constants/index.js';
import { eventBus, EVENTS } from '../services/EventBus.js';
import { showToast } from './ToastController.js';
import { escapeHtml } from '../utils/index.js';

// Module state
let authService = null;
let storageService = null;
let favoritesService = null;
let recentPatchesService = null;

/**
 * Initialize the MigrationController
 * @param {Object} deps - Dependencies
 * @param {Object} deps.authService - Auth service instance
 * @param {Object} deps.storageService - Storage service instance
 * @param {Object} deps.favoritesService - Favorites service instance
 * @param {Object} deps.recentPatchesService - Recent patches service instance
 * @returns {Object} Public methods for onclick handlers
 */
export function initMigrationController(deps) {
    authService = deps.authService;
    storageService = deps.storageService;
    favoritesService = deps.favoritesService;
    recentPatchesService = deps.recentPatchesService;
    
    // Subscribe to auth changes to trigger migration check on login
    eventBus.on(EVENTS.AUTH_CHANGED, ({ isLoggedIn, user }) => {
        if (isLoggedIn && user) {
            checkForMigration();
        }
    });
    
    // Return public methods for window/onclick exposure
    return {
        checkForMigration,
        closeMigrationModal,
        migratePatches
    };
}

/**
 * Check if user has local patches that could be migrated to cloud
 * Called when user signs in
 */
async function checkForMigration() {
    // Only prompt once per user (check localStorage flag)
    const userId = authService.getCurrentUser()?.id;
    if (!userId) return;
    
    const migratedKey = `${MIGRATION_PROMPTED_KEY}-${userId}`;
    if (localStorage.getItem(migratedKey) === 'true') return;
    
    // Check for local data to migrate via unified storage service
    if (!storageService) return;
    
    const hasData = await storageService.hasLocalDataToMigrate();
    if (!hasData) {
        // No data to migrate, mark as done
        localStorage.setItem(migratedKey, 'true');
        return;
    }
    
    // Get local data for preview
    const localData = await storageService.getLocalDataForMigration();
    const localPatches = localData.patches || [];
    
    if (localPatches.length === 0 && !localData.workingPatch && (localData.versions || []).length === 0) {
        localStorage.setItem(migratedKey, 'true');
        return;
    }
    
    // Show migration modal
    showMigrationModal(localPatches);
}

/**
 * Get local patches that could be migrated (legacy, now delegated to storageService)
 * @returns {Promise<Array>} Array of local patch objects
 */
async function getLocalPatchesForMigration() {
    if (!storageService) return [];
    const localData = await storageService.getLocalDataForMigration();
    return localData.patches || [];
}

/**
 * Show the migration modal
 * @param {Array} localPatches - Array of local patch objects to display
 */
function showMigrationModal(localPatches) {
    const modal = document.getElementById(MODAL_IDS.MIGRATION);
    const countEl = document.getElementById('migration-patch-count');
    const previewEl = document.getElementById('migration-preview');
    
    countEl.textContent = localPatches.length;
    
    // Build preview list
    let previewHtml = '';
    localPatches.forEach(p => {
        const deviceCount = p.deviceCount || 0;
        const connectionCount = p.connectionCount || 0;
        previewHtml += `
            <div style="display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid rgba(255,255,255,0.1);">
                <span style="color: #fff;">${escapeHtml(p.name)}</span>
                <span style="color: #666; font-size: 0.85rem;">${deviceCount} devices, ${connectionCount} cables</span>
            </div>
        `;
    });
    previewEl.innerHTML = previewHtml;
    
    modal.classList.add('active');
}

/**
 * Close the migration modal
 */
function closeMigrationModal() {
    document.getElementById(MODAL_IDS.MIGRATION).classList.remove('active');
}

/**
 * Handle migration action
 * @param {string} action - 'move' (delete local after), 'copy' (keep local), 'skip' (don't migrate)
 */
async function migratePatches(action) {
    const userId = authService.getCurrentUser()?.id;
    if (!userId) {
        closeMigrationModal();
        return;
    }
    
    const migratedKey = `${MIGRATION_PROMPTED_KEY}-${userId}`;
    
    if (action === 'skip') {
        // Mark as prompted so we don't ask again
        localStorage.setItem(migratedKey, 'true');
        closeMigrationModal();
        showToast('Data kept local. Your cloud storage is empty.');
        return;
    }
    
    // Use unified storage service for migration
    if (!storageService) {
        closeMigrationModal();
        return;
    }
    
    showToast('Migrating data to cloud...');
    
    // Perform migration via unified storage service
    const result = await storageService.migrateLocalToCloud(action);
    
    // Also migrate favorites and recents
    try {
        await favoritesService.loadFromCloud();
    } catch (e) {
        console.warn('Failed to migrate favorites:', e);
    }
    
    try {
        await recentPatchesService.loadFromCloud();
    } catch (e) {
        console.warn('Failed to migrate recents:', e);
    }
    
    // If action is 'move', also clear local storage
    if (action === 'move') {
        try {
            // Import constant directly for this specific operation
            const FAVORITES_STORAGE_KEY = 'patchwerk-favorites';
            const RECENT_PATCHES_STORAGE_KEY = 'patchwerk-recent-patches';
            localStorage.removeItem(FAVORITES_STORAGE_KEY);
            localStorage.removeItem(RECENT_PATCHES_STORAGE_KEY);
        } catch (e) {
            console.warn('Failed to clear local favorites/recents:', e);
        }
    }
    
    // Mark migration as complete
    localStorage.setItem(migratedKey, 'true');
    
    closeMigrationModal();
    
    // Show result
    if (result.success) {
        const deleteMsg = action === 'move' ? ' Local copies removed.' : ' Local copies kept.';
        showToast(`✓ ${result.migratedCount} item(s) migrated to cloud!${deleteMsg}`);
    } else {
        showToast(`Migrated ${result.migratedCount} item(s), some errors: ${result.errors.join(', ')}`, true);
    }
}
